"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { cn } from "../../lib/utils";
import type { FilterDropdownButtonProps } from "./filter-dropdown-button.types";
export type { FilterDropdownButtonOption, FilterDropdownButtonProps } from "./filter-dropdown-button.types";

export function FilterDropdownButton({
  options,
  value,
  onValueChange,
  ariaLabel,
  className,
  align = "end",
  icon,
  labelDisplay = "full",
  trigger,
}: FilterDropdownButtonProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = React.useId();
  const Trigger = trigger ? Slot : "button";

  const selectedOption =
    options.find((option) => option.value === value) ?? options[0];

  const focusOption = React.useCallback((index: number) => {
    itemRefs.current[index]?.focus();
  }, []);

  const closeMenu = React.useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const moveFocus = React.useCallback(
    (currentIndex: number, direction: 1 | -1) => {
      if (options.length === 0) return;

      let nextIndex = currentIndex;
      for (let step = 0; step < options.length; step += 1) {
        nextIndex = (nextIndex + direction + options.length) % options.length;
        if (!options[nextIndex]?.disabled) {
          focusOption(nextIndex);
          return;
        }
      }
    },
    [focusOption, options],
  );

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeMenu, isOpen]);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const selectedIndex = options.findIndex(
      (option) => option.value === selectedOption?.value && !option.disabled,
    );
    const fallbackIndex = options.findIndex((option) => !option.disabled);
    const nextIndex = selectedIndex >= 0 ? selectedIndex : fallbackIndex;

    if (nextIndex < 0) return undefined;

    const frame = requestAnimationFrame(() => {
      focusOption(nextIndex);
    });

    return () => cancelAnimationFrame(frame);
  }, [focusOption, isOpen, options, selectedOption?.value]);

  return (
    <div ref={containerRef} className={cn("relative inline-flex", className)}>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        className={cn(
          !trigger && "inline-flex h-8 w-full items-center justify-between gap-1 rounded-[8px] bg-white px-1.5 border border-[#D1D1D6] text-xs font-medium text-[#344054] transition-[transform,box-shadow] duration-200 ease-out",
          !trigger && labelDisplay === "icon" &&
          "h-8 w-8 justify-center rounded-[8px] p-0",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0D41E1]",
        )}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" ||
            event.key === " " ||
            event.key === "ArrowDown"
          ) {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        {trigger ?? (
          <>
            <span
              className={cn(
                "flex min-w-0 items-center gap-1.5",
                labelDisplay === "icon" && "justify-center gap-0",
              )}
            >
              {icon ?? (
                <SlidersHorizontal className="h-3 w-3 shrink-0 text-[#667085]" />
              )}
              {labelDisplay === "full" ? (
                <span className="truncate">{selectedOption?.label ?? ""}</span>
              ) : null}
            </span>
            {labelDisplay === "full" ? (
              <ChevronDown
                className={cn(
                  "h-3 w-3 shrink-0 text-[#667085] transition-transform duration-200 ease-out",
                  isOpen && "rotate-180",
                )}
              />
            ) : null}
          </>
        )}
      </Trigger>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            id={menuId}
            role="menu"
            aria-label={ariaLabel}
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className={cn(
              "absolute top-[calc(100%+0.375rem)] z-50 min-w-full rounded-2xl border border-[#D0D5DD] bg-white p-1.5 shadow-[0_16px_32px_rgba(16,24,40,0.16)]",
              labelDisplay === "icon" && "min-w-[148px]",
              trigger && "min-w-52",
              align === "start" ? "left-0" : "right-0",
            )}
          >
            <div className="space-y-1">
              {options.map((option, index) => {
                const isSelected = option.value === value;

                return (
                  <button
                    key={option.value}
                    ref={(node) => {
                      itemRefs.current[index] = node;
                    }}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isSelected}
                    disabled={option.disabled}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-medium transition-colors outline-none",
                      isSelected
                        ? "bg-[#F2F4F7] text-[#101828]"
                        : "text-[#475467] hover:bg-[#F9FAFB] hover:text-[#101828]",
                      "focus:bg-[#F2F4F7] focus:text-[#101828]",
                      option.disabled && "cursor-not-allowed opacity-50",
                    )}
                    onClick={() => {
                      if (option.disabled) return;
                      onValueChange(option.value);
                      closeMenu();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "ArrowDown") {
                        event.preventDefault();
                        moveFocus(index, 1);
                      }

                      if (event.key === "ArrowUp") {
                        event.preventDefault();
                        moveFocus(index, -1);
                      }

                      if (event.key === "Home") {
                        event.preventDefault();
                        const firstEnabledIndex = options.findIndex(
                          (item) => !item.disabled,
                        );
                        if (firstEnabledIndex >= 0) {
                          focusOption(firstEnabledIndex);
                        }
                      }

                      if (event.key === "End") {
                        event.preventDefault();
                        const lastEnabledIndex = [...options]
                          .map((item, itemIndex) => ({ item, itemIndex }))
                          .reverse()
                          .find(({ item }) => !item.disabled)?.itemIndex;

                        if (lastEnabledIndex !== undefined) {
                          focusOption(lastEnabledIndex);
                        }
                      }

                      if (event.key === "Escape") {
                        event.preventDefault();
                        closeMenu();
                      }
                    }}
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{option.label}</span>
                      {option.description ? (
                        <span className="mt-0.5 text-[11px] font-normal leading-4 text-[#667085]">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    {isSelected ? (
                      <span className="h-2 w-2 shrink-0 rounded-full bg-[#0D41E1]" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
